import React, { useState } from 'react';
import { useInfiniteQuery, useQueryClient } from 'react-query';
import { useAuth } from '../contexts/AuthContext';
import { postsAPI } from '../api/posts';
import { useNavigate } from 'react-router-dom';
import './TrashPage.css';

const TrashPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [processingPostId, setProcessingPostId] = useState(null);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    refetch,
  } = useInfiniteQuery(
    'trash',
    ({ pageParam = 0 }) => postsAPI.getTrash(pageParam, 20),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.last) return undefined;
        return lastPage.pageable.pageNumber + 1;
      },
      refetchOnWindowFocus: false,
    }
  );

  const posts = data?.pages.flatMap((page) => page.content) || [];

  // 관리자가 아니면 접근 불가
  if (!user || user.role !== 'ADMIN') {
    return (
      <div className="trash-error">
        <p>관리자만 접근할 수 있습니다.</p>
        <button onClick={() => navigate('/feed')}>피드로 돌아가기</button>
      </div>
    );
  }

  // 복구
  const handleRestore = async (postId) => {
    if (window.confirm('이 게시물을 복구하시겠습니까?')) {
      setProcessingPostId(postId);
      try {
        await postsAPI.restorePost(postId);
        queryClient.invalidateQueries('trash');
        queryClient.invalidateQueries('feed');
        alert('게시물이 복구되었습니다.');
      } catch (error) {
        console.error('복구 실패:', error);
        alert('복구에 실패했습니다.');
      } finally {
        setProcessingPostId(null);
      }
    }
  };

  // 영구 삭제
  const handlePermanentDelete = async (postId) => {
    if (window.confirm('⚠️ 경고: 영구 삭제하면 복구할 수 없습니다.\n정말 삭제하시겠습니까?')) {
      setProcessingPostId(postId);
      try {
        await postsAPI.permanentDeletePost(postId);
        queryClient.invalidateQueries('trash');
        alert('게시물이 영구 삭제되었습니다.');
      } catch (error) {
        console.error('영구 삭제 실패:', error);
        alert('영구 삭제에 실패했습니다.');
      } finally {
        setProcessingPostId(null);
      }
    }
  };

  // 전체 비우기
  const handleEmptyTrash = async () => {
    if (window.confirm('⚠️ 경고: 휴지통의 모든 게시물을 영구 삭제합니다.\n복구할 수 없습니다. 정말 진행하시겠습니까?')) {
      try {
        // 모든 게시물 영구 삭제
        await Promise.all(posts.map(post => postsAPI.permanentDeletePost(post.postId)));
        queryClient.invalidateQueries('trash');
        alert('휴지통이 비워졌습니다.');
      } catch (error) {
        console.error('휴지통 비우기 실패:', error);
        alert('휴지통 비우기에 실패했습니다.');
      }
    }
  };

  if (isLoading) {
    return <div className="trash-loading">로딩 중...</div>;
  }

  if (isError) {
    return (
      <div className="trash-error">
        <p>휴지통을 불러올 수 없습니다.</p>
        <button onClick={() => refetch()}>다시 시도</button>
      </div>
    );
  }

  return (
    <div className="trash-container">
      <div className="trash-header">
        <h1>🗑️ 휴지통</h1>
        <p>삭제된 게시물을 복구하거나 영구 삭제할 수 있습니다.</p>
        {posts.length > 0 && (
          <button className="btn-empty-trash" onClick={handleEmptyTrash}>
            휴지통 비우기
          </button>
        )}
      </div>

      {posts.length === 0 ? (
        <div className="trash-empty">
          <p>휴지통이 비어있습니다.</p>
        </div>
      ) : (
        <div className="trash-grid">
          {posts.map((post) => {
            const thumbnail = post.media?.[0];
            const isProcessing = processingPostId === post.postId;

            return (
              <div key={post.postId} className="trash-item">
                <div className="trash-item-media">
                  {thumbnail?.mediaType === 'IMAGE' ? (
                    <img src={thumbnail.mediaUrl} alt="게시물" />
                  ) : thumbnail?.mediaType === 'VIDEO' ? (
                    <video src={thumbnail.mediaUrl} />
                  ) : (
                    <div className="no-media">미디어 없음</div>
                  )}
                  {post.media?.length > 1 && (
                    <div className="media-count">
                      📷 {post.media.length}
                    </div>
                  )}
                </div>

                <div className="trash-item-info">
                  <div className="trash-item-user">
                    <span className="username">{post.user.username}</span>
                    <span className="deleted-date">
                      {new Date(post.deletedAt).toLocaleDateString('ko-KR')} 삭제
                    </span>
                  </div>

                  {post.caption && (
                    <p className="trash-item-caption">{post.caption}</p>
                  )}

                  {post.hashtags && post.hashtags.length > 0 && (
                    <div className="trash-item-hashtags">
                      {post.hashtags.map((tag, idx) => (
                        <span key={idx} className="hashtag">#{tag}</span>
                      ))}
                    </div>
                  )}

                  <div className="trash-item-actions">
                    <button
                      className="btn-restore"
                      onClick={() => handleRestore(post.postId)}
                      disabled={isProcessing}
                    >
                      {isProcessing ? '처리 중...' : '복구'}
                    </button>
                    <button
                      className="btn-permanent-delete"
                      onClick={() => handlePermanentDelete(post.postId)}
                      disabled={isProcessing}
                    >
                      {isProcessing ? '처리 중...' : '영구 삭제'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {hasNextPage && (
        <div className="trash-load-more">
          <button onClick={fetchNextPage} disabled={isFetchingNextPage}>
            {isFetchingNextPage ? '불러오는 중...' : '더 보기'}
          </button>
        </div>
      )}
    </div>
  );
};

export default TrashPage;

